import React from 'react';
import {
  Plane,
  Search,
  Calendar,
  Users,
  Luggage,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  ArrowLeftRight,
  Loader2,
  Sparkles,
  Mail,
  Package,
  Info,
} from 'lucide-react';
import { createFlight, searchFlights } from '../lib/api';
import FlightQuoteModal, { type DepartureTimeFilter, type FlightQuoteSearchContext } from '../components/FlightQuoteModal';
import AirportAutocompleteInput from '../components/AirportAutocompleteInput';
import { loadClients, saveClients } from '../lib/skyluxStore';
import type { Client, Flight } from '../types';
import { MOCK_CLIENTS } from '../types';
import MissingAgentNotice from '../components/MissingAgentNotice';
import { useAgent } from '../context/AgentsContext';
import { useParams } from 'react-router-dom';

function departureTimeBucketFromFlight(f: Flight): DepartureTimeFilter {
  const h = f.departureHour;
  if (typeof h !== 'number' || Number.isNaN(h)) return 'any';
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  return 'evening';
}

const fieldLabel: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: '0.4rem',
  display: 'block',
};

const fieldHint: React.CSSProperties = {
  display: 'block',
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  lineHeight: 1.45,
  marginTop: '0.35rem',
  fontWeight: 500,
  textTransform: 'none',
  letterSpacing: 'normal',
};

const card = {
  background: 'white',
  border: '1px solid var(--border)',
  borderRadius: '1.25rem',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
} as const;

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  border: 'none',
  borderRadius: '0.75rem',
  padding: '0.75rem 1.35rem',
  fontWeight: 800,
  fontSize: '0.9rem',
  cursor: 'pointer',
  background: 'var(--accent)',
  color: 'white',
  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.28)',
};

const btnSecondary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.45rem',
  border: '1px solid var(--border)',
  borderRadius: '0.65rem',
  padding: '0.55rem 1rem',
  fontWeight: 700,
  fontSize: '0.85rem',
  cursor: 'pointer',
  background: '#f8fafc',
  color: '#0f172a',
};

const btnSuccess: React.CSSProperties = {
  ...btnSecondary,
  background: '#0f766e',
  color: 'white',
  borderColor: '#0f766e',
  boxShadow: '0 6px 16px rgba(15, 118, 110, 0.22)',
};

const btnDark: React.CSSProperties = {
  ...btnSecondary,
  background: 'var(--primary)',
  color: 'white',
  borderColor: 'transparent',
};

type FlightSearchMetadata = {
  id?: string;
  status?: string;
  google_flights_url?: string;
  total_time_taken?: number;
};

type PriceInsights = {
  lowest_price?: number;
  price_level?: string;
  typical_price_range?: number[];
};

const AdminFlightSearch = () => {
  const { agentId } = useParams();
  const agent = useAgent(agentId);
  const [tripType, setTripType] = React.useState<'1' | '2'>('1');
  const [departureId, setDepartureId] = React.useState('');
  const [arrivalId, setArrivalId] = React.useState('');
  const [outboundDate, setOutboundDate] = React.useState('');
  const [returnDate, setReturnDate] = React.useState('');
  const [adults, setAdults] = React.useState(1);
  const [bags, setBags] = React.useState(0);
  const [stops, setStops] = React.useState<'0' | '1' | '2' | '3'>('0');
  const [travelClass, setTravelClass] = React.useState<'1' | '2' | '3' | '4'>('1');
  const [airlineFilter, setAirlineFilter] = React.useState('');
  const [showMoreOptions, setShowMoreOptions] = React.useState(false);
  const [rows, setRows] = React.useState<Flight[]>([]);
  const [searchMetadata, setSearchMetadata] = React.useState<FlightSearchMetadata | null>(null);
  const [priceInsights, setPriceInsights] = React.useState<PriceInsights | null>(null);
  const [clients, setClients] = React.useState<Client[]>(() => loadClients(MOCK_CLIENTS));
  const [expandedFlightId, setExpandedFlightId] = React.useState<string | null>(null);
  const [quoteFlight, setQuoteFlight] = React.useState<Flight | null>(null);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [quoteOpen, setQuoteOpen] = React.useState(false);
  const [quoteSearchContext, setQuoteSearchContext] = React.useState<FlightQuoteSearchContext | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [savingFlight, setSavingFlight] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [inventoryMessage, setInventoryMessage] = React.useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const visibleRows = React.useMemo(() => {
    const q = airlineFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.airline.toLowerCase().includes(q));
  }, [rows, airlineFilter]);

  function swapRoute() {
    const a = departureId;
    setDepartureId(arrivalId);
    setArrivalId(a);
  }

  async function handleSearch(e?: React.FormEvent) {
    e?.preventDefault();
    if (!departureId.trim() || !arrivalId.trim() || !outboundDate.trim()) {
      setError('Choose origin, destination, and departure date to search.');
      return;
    }
    if (tripType === '1' && !returnDate.trim()) {
      setError('Round trip needs a return date.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setInventoryMessage(null);
      setExpandedFlightId(null);
      const result = await searchFlights({
        type: tripType,
        departureId: departureId.trim().toUpperCase(),
        arrivalId: arrivalId.trim().toUpperCase(),
        outboundDate: outboundDate.trim(),
        returnDate: tripType === '1' ? returnDate.trim() : undefined,
        adults,
        bags,
        stops,
        travelClass,
        deepSearch: true,
      });
      setRows(result.flights ?? []);
      setSearchMetadata((result.searchMetadata as FlightSearchMetadata) ?? null);
      setPriceInsights((result.priceInsights as PriceInsights) ?? null);
    } catch (err) {
      setRows([]);
      setSearchMetadata(null);
      setPriceInsights(null);
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  function openQuote(flight: Flight, clientId?: string) {
    setQuoteSearchContext({
      tripType,
      departureId: departureId.trim().toUpperCase(),
      arrivalId: arrivalId.trim().toUpperCase(),
      outboundDate: outboundDate.trim(),
      returnDate: tripType === '1' ? returnDate.trim() : '',
      adults,
      bags,
      stops,
      travelClass,
      airlineFilter: airlineFilter.trim(),
      timeBucket: departureTimeBucketFromFlight(flight),
      liveFlights:
        tripType === '1' && rows.length > 0 && !rows.some((f) => f.departureToken?.trim()) ? [] : rows,
    });
    setQuoteFlight(flight);
    setSelectedClientId(clientId ?? '');
    setQuoteOpen(true);
    setExpandedFlightId(flight.id);
  }

  const selectedClient = React.useMemo(
    () => clients.find((c) => c.id === selectedClientId) ?? null,
    [clients, selectedClientId]
  );

  const upsertClient = React.useCallback((client: Client) => {
    setClients((prev) => {
      const exists = prev.some((c) => c.id === client.id);
      const next = exists ? prev.map((c) => (c.id === client.id ? client : c)) : [client, ...prev];
      saveClients(next);
      return next;
    });
  }, []);

  async function addToInventory(flight: Flight) {
    try {
      setSavingFlight(true);
      setInventoryMessage(null);
      const { id, ...payload } = flight;
      await createFlight(payload);
      setError(null);
      setInventoryMessage({ type: 'ok', text: 'Saved to flight inventory.' });
    } catch (err) {
      setInventoryMessage({ type: 'err', text: err instanceof Error ? err.message : 'Could not save flight.' });
    } finally {
      setSavingFlight(false);
    }
  }

  const tripToggle = (value: '1' | '2', label: string) => (
    <button
      key={value}
      type="button"
      onClick={() => setTripType(value)}
      style={{
        flex: 1,
        padding: '0.65rem 1rem',
        borderRadius: '0.65rem',
        border: 'none',
        fontWeight: 800,
        fontSize: '0.85rem',
        cursor: 'pointer',
        transition: 'background 0.15s, color 0.15s',
        background: tripType === value ? 'white' : 'transparent',
        color: tripType === value ? 'var(--accent)' : 'var(--text-muted)',
        boxShadow: tripType === value ? '0 1px 4px rgba(15, 23, 42, 0.08)' : 'none',
      }}
    >
      {label}
    </button>
  );

  if (!agent) {
    return (
      <div className="animate-fade" style={{ maxWidth: '1120px' }}>
        <MissingAgentNotice />
      </div>
    );
  }

  return (
    <div className="animate-fade" style={{ maxWidth: '1120px' }}>
      <header style={{ marginBottom: '1.75rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.1rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 0.5rem' }}>
              Flight search
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', margin: 0, maxWidth: '52ch', lineHeight: 1.55 }}>
              Enter route and dates, then search live inventory. Open a result to add it to your catalog or send a branded quote email to a new or existing client.
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.6rem',
              padding: '0.75rem 1rem',
              borderRadius: '0.85rem',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              color: '#1e40af',
              fontSize: '0.8rem',
              maxWidth: '320px',
              lineHeight: 1.45,
            }}
          >
            <Info size={18} style={{ flexShrink: 0, marginTop: '0.1rem' }} aria-hidden />
            <span>
              <strong>Tip:</strong> type city or IATA code in From / To. Round trip needs both dates. The airline box only filters rows after you search.
            </span>
          </div>
        </div>
      </header>

      <form onSubmit={(e) => void handleSearch(e)} style={{ ...card, padding: '1.5rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, var(--accent), #6366f1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Search size={20} aria-hidden />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800 }}>Search parameters</h2>
              <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Required: origin, destination, departure date</p>
            </div>
          </div>
          <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.85 : 1, cursor: loading ? 'wait' : 'pointer' }}>
            {loading ? <Loader2 size={20} style={{ animation: 'spin 0.75s linear infinite' }} /> : <Sparkles size={18} aria-hidden />}
            {loading ? 'Searching…' : 'Search flights'}
          </button>
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <span style={fieldLabel}>Trip type</span>
          <div
            role="group"
            aria-label="Trip type"
            style={{ display: 'flex', padding: '0.25rem', background: '#f1f5f9', borderRadius: '0.75rem', maxWidth: '360px' }}
          >
            {tripToggle('2', 'One way')}
            {tripToggle('1', 'Round trip')}
          </div>
          <span style={fieldHint}>
            <strong>One way</strong> searches outbound only. <strong>Round trip</strong> requires a return date; both legs use the same passenger counts below.
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
            gap: '1rem 1.25rem',
            alignItems: 'end',
            marginBottom: '1.25rem',
          }}
        >
          <div>
            <span style={fieldLabel}>From (origin)</span>
            <AirportAutocompleteInput value={departureId} onChange={setDepartureId} placeholder="City or code (e.g. JFK)" showResolved />
            <span style={fieldHint}>What you type is matched to an airport. <strong>Resolved</strong> shows the IATA-style ID sent to the live search (e.g. RMO → that airport).</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: '0.15rem' }}>
            <button
              type="button"
              onClick={swapRoute}
              aria-label="Swap origin and destination"
              title="Swap origin and destination"
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: '1px solid var(--border)',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--accent)',
              }}
            >
              <ArrowLeftRight size={20} />
            </button>
          </div>
          <div>
            <span style={fieldLabel}>To (destination)</span>
            <AirportAutocompleteInput value={arrivalId} onChange={setArrivalId} placeholder="City or code (e.g. LHR)" showResolved />
            <span style={fieldHint}>Same as From: autocomplete + resolution. Origin and destination must not be the same airport.</span>
          </div>
          <div>
            <span style={fieldLabel}>Depart (outbound)</span>
            <div style={{ position: 'relative' }}>
              <Calendar
                size={16}
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}
                aria-hidden
              />
              <input
                type="date"
                value={outboundDate}
                onChange={(e) => setOutboundDate(e.target.value)}
                style={{ paddingLeft: '2.35rem' }}
                aria-describedby="flight-search-depart-hint"
              />
            </div>
            <span id="flight-search-depart-hint" style={fieldHint}>
              First travel date (stored as <code style={{ fontSize: '0.7rem' }}>YYYY-MM-DD</code>). The browser may show mm/dd/yyyy in the picker, but the value sent is always the ISO date.
            </span>
          </div>
          <div>
            <span style={fieldLabel}>Return {tripType === '1' ? '(required)' : '(one way)'}</span>
            <div style={{ position: 'relative' }}>
              <Calendar
                size={16}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                  pointerEvents: 'none',
                  opacity: tripType === '1' ? 1 : 0.35,
                }}
                aria-hidden
              />
              <input
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                disabled={tripType !== '1'}
                style={{ paddingLeft: '2.35rem', opacity: tripType === '1' ? 1 : 0.55 }}
                aria-describedby="flight-search-return-hint"
              />
            </div>
            <span id="flight-search-return-hint" style={fieldHint}>
              {tripType === '1' ? 'Inbound date for the round trip (same format as Depart).' : 'Disabled for one way — switch to Round trip to set this.'}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowMoreOptions((v) => !v)}
          style={{
            ...btnSecondary,
            width: '100%',
            justifyContent: 'center',
            marginBottom: showMoreOptions ? '1rem' : 0,
            background: '#f8fafc',
          }}
        >
          <SlidersHorizontal size={16} aria-hidden />
          Travelers & cabin
          {showMoreOptions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {showMoreOptions && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '1rem',
              padding: '1.1rem',
              background: '#f8fafc',
              borderRadius: '0.85rem',
              border: '1px solid var(--border)',
              marginBottom: '1rem',
            }}
          >
            <div>
              <span style={fieldLabel}>
                <Users size={12} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} aria-hidden />
                Adults
              </span>
              <input type="number" min={1} max={9} value={adults} onChange={(e) => setAdults(Math.max(1, Math.min(9, Number(e.target.value) || 1)))} />
              <span style={fieldHint}>How many adult tickets to price (1–9). Sent to the search API as <code style={{ fontSize: '0.7rem' }}>adults</code>.</span>
            </div>
            <div>
              <span style={fieldLabel}>
                <Luggage size={12} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} aria-hidden />
                Bags
              </span>
              <input type="number" min={0} max={9} value={bags} onChange={(e) => setBags(Math.max(0, Math.min(9, Number(e.target.value) || 0)))} />
              <span style={fieldHint}>Bag count passed into the live search (0–9). This is a search parameter, not a specific airline baggage policy.</span>
            </div>
            <div>
              <span style={fieldLabel}>Stops</span>
              <select value={stops} onChange={(e) => setStops(e.target.value as '0' | '1' | '2' | '3')}>
                <option value="0">Any</option>
                <option value="1">Nonstop</option>
                <option value="2">≤ 1 stop</option>
                <option value="3">≤ 2 stops</option>
              </select>
              <span style={fieldHint}>
                Google Flights <code style={{ fontSize: '0.7rem' }}>stops</code> filter: <strong>0</strong> any · <strong>1</strong> nonstop only · <strong>2</strong> up to 1 stop · <strong>3</strong> up to 2 stops.
              </span>
            </div>
            <div>
              <span style={fieldLabel}>Cabin</span>
              <select value={travelClass} onChange={(e) => setTravelClass(e.target.value as '1' | '2' | '3' | '4')}>
                <option value="1">Economy</option>
                <option value="2">Premium economy</option>
                <option value="3">Business</option>
                <option value="4">First</option>
              </select>
              <span style={fieldHint}>
                Cabin tier sent as <code style={{ fontSize: '0.7rem' }}>travel_class</code>: <strong>1</strong> Economy · <strong>2</strong> Premium economy · <strong>3</strong> Business · <strong>4</strong> First.
              </span>
            </div>
          </div>
        )}
        <details
          style={{
            marginTop: showMoreOptions ? '0.25rem' : '1rem',
            padding: '0.85rem 1rem',
            borderRadius: '0.75rem',
            border: '1px solid var(--border)',
            background: '#fafbfc',
            fontSize: '0.82rem',
            color: 'var(--text-main)',
          }}
        >
          <summary style={{ cursor: 'pointer', fontWeight: 800, color: 'var(--accent)', userSelect: 'none' }}>
            Field reference (API / UI)
          </summary>
          <ul style={{ margin: '0.75rem 0 0', paddingLeft: '1.15rem', lineHeight: 1.55, color: 'var(--text-muted)' }}>
            <li>
              <strong style={{ color: 'var(--text-main)' }}>Trip type</strong> — One way = outbound only. Round trip = outbound + return date.
            </li>
            <li>
              <strong style={{ color: 'var(--text-main)' }}>From / To</strong> — Free text or IATA. “Resolved” is the airport ID used in the request after backend lookup.
            </li>
            <li>
              <strong style={{ color: 'var(--text-main)' }}>Depart / Return</strong> — ISO dates <code>YYYY-MM-DD</code>. Browser locale may show another format in the picker.
            </li>
            <li>
              <strong style={{ color: 'var(--text-main)' }}>Adults / Bags</strong> — Integers 1–9 and 0–9; forwarded as <code>adults</code> and <code>bags</code> to Google Flights via SerpApi.
            </li>
            <li>
              <strong style={{ color: 'var(--text-main)' }}>Stops</strong> — Maps to provider <code>stops</code>: 0 any, 1 nonstop, 2 ≤1 stop, 3 ≤2 stops.
            </li>
            <li>
              <strong style={{ color: 'var(--text-main)' }}>Cabin</strong> — Maps to <code>travel_class</code> 1–4 as labeled in the dropdown.
            </li>
            <li>
              <strong style={{ color: 'var(--text-main)' }}>Filter airline in results</strong> — After a search, narrows the list by airline name substring only (does not change the API request).
            </li>
          </ul>
        </details>
      </form>

      {error && (
        <div
          role="alert"
          style={{
            padding: '0.85rem 1.1rem',
            borderRadius: '0.85rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            marginBottom: '1rem',
            fontSize: '0.9rem',
          }}
        >
          {error}
        </div>
      )}

      {inventoryMessage && (
        <div
          role="status"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: '0.75rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            background: inventoryMessage.type === 'ok' ? '#ecfdf5' : '#fef2f2',
            color: inventoryMessage.type === 'ok' ? '#047857' : '#991b1b',
            border: `1px solid ${inventoryMessage.type === 'ok' ? '#a7f3d0' : '#fecaca'}`,
          }}
        >
          {inventoryMessage.text}
        </div>
      )}

      <section style={{ ...card, padding: '1.35rem 1.5rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-main)', fontWeight: 700 }}>
            <Plane size={18} color="var(--accent)" aria-hidden />
            Results
            <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {visibleRows.length} flight{visibleRows.length === 1 ? '' : 's'}
              {rows.length !== visibleRows.length ? ` (filtered from ${rows.length})` : ''}
            </span>
          </div>
          <div style={{ flex: '1 1 220px', maxWidth: '320px', minWidth: '160px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
              <input
                value={airlineFilter}
                onChange={(e) => setAirlineFilter(e.target.value)}
                placeholder="Filter by airline in results…"
                aria-label="Filter results by airline"
                aria-describedby="flight-search-airline-filter-hint"
                style={{ paddingLeft: '2.35rem' }}
              />
            </div>
            <span id="flight-search-airline-filter-hint" style={{ ...fieldHint, marginTop: '0.4rem' }}>
              Client-side only: hides rows whose airline name does not contain this text (case-insensitive). Leave empty to show all results.
            </span>
          </div>
        </div>

        {selectedClientId && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.65rem 0.9rem',
              borderRadius: '0.65rem',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              fontSize: '0.82rem',
              color: '#92400e',
            }}
          >
            <strong>Quote recipient:</strong> {clients.find((c) => c.id === selectedClientId)?.name ?? 'Unknown'}
            {' · '}
            <button type="button" onClick={() => setSelectedClientId('')} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
              Clear
            </button>
          </div>
        )}

        {priceInsights && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '1rem 1.15rem',
              borderRadius: '0.85rem',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Price insights</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>
              Lowest <strong style={{ color: 'var(--text-main)' }}>${priceInsights.lowest_price ?? '—'}</strong>
              {priceInsights.price_level ? <span> · {priceInsights.price_level}</span> : null}
            </div>
            {priceInsights.typical_price_range && priceInsights.typical_price_range.length === 2 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginTop: '0.25rem' }}>
                Typical range ${priceInsights.typical_price_range[0]} – ${priceInsights.typical_price_range[1]}
              </div>
            ) : null}
          </div>
        )}

        {searchMetadata ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginBottom: '1rem' }}>
            Status <code style={{ background: '#f1f5f9', padding: '0.1rem 0.35rem', borderRadius: '4px' }}>{searchMetadata.status ?? 'ok'}</code>
            {typeof searchMetadata.total_time_taken === 'number' ? <span> · {searchMetadata.total_time_taken}s</span> : null}
          </div>
        ) : null}

        {visibleRows.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '2.5rem 1.5rem',
              color: 'var(--text-muted)',
              border: '1px dashed var(--border)',
              borderRadius: '1rem',
              background: '#fafafa',
            }}
          >
            <Plane size={32} style={{ opacity: 0.35, marginBottom: '0.75rem' }} aria-hidden />
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-main)' }}>No flights to show</p>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.9rem', maxWidth: '36ch', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
              Submit the form above to run a live search. If you already searched, try relaxing the airline filter.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.85rem' }}>
            {visibleRows.map((flight) => {
              const expanded = expandedFlightId === flight.id;
              return (
                <article
                  key={flight.id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    background: 'white',
                    boxShadow: expanded ? '0 4px 20px rgba(59, 130, 246, 0.12)' : 'none',
                    borderColor: expanded ? '#bfdbfe' : 'var(--border)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'stretch',
                      justifyContent: 'space-between',
                      gap: '0.75rem',
                      flexWrap: 'wrap',
                      padding: '1rem 1.1rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0, flex: '1 1 240px' }}>
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          background: expanded ? '#eff6ff' : '#f8fafc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Plane size={22} color="var(--accent)" aria-hidden />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 800, fontSize: '1rem' }}>
                          {flight.airline} <span style={{ fontWeight: 700, color: 'var(--text-muted)' }}>{flight.flightNumber}</span>
                        </p>
                        <p style={{ margin: '0.2rem 0 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          {flight.departure} → {flight.arrival}
                          {flight.travelDate ? ` · ${flight.travelDate}` : ''}
                        </p>
                        <p style={{ margin: '0.15rem 0 0', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                          {typeof flight.totalDuration === 'number' ? `${flight.totalDuration} min` : 'Duration n/a'}
                          {typeof flight.stopsCount === 'number'
                            ? ` · ${flight.stopsCount === 0 ? 'Nonstop' : `${flight.stopsCount} stop${flight.stopsCount === 1 ? '' : 's'}`}`
                            : ''}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem' }}>
                          {flight.departureTime} – {flight.arrivalTime}
                        </p>
                        <p style={{ margin: '0.15rem 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>Local times</p>
                        <p style={{ margin: '0.35rem 0 0', fontSize: '1.35rem', fontWeight: 900, color: 'var(--primary)' }}>${flight.price}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedFlightId((id) => (id === flight.id ? null : flight.id))}
                        aria-expanded={expanded}
                        aria-controls={`flight-panel-${flight.id}`}
                        style={{
                          alignSelf: 'center',
                          width: '44px',
                          height: '44px',
                          borderRadius: '12px',
                          border: '1px solid var(--border)',
                          background: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {expanded ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
                      </button>
                    </div>
                  </div>

                  <div
                    id={`flight-panel-${flight.id}`}
                    style={{
                      maxHeight: expanded ? '2000px' : 0,
                      opacity: expanded ? 1 : 0,
                      transition: 'max-height 0.35s ease, opacity 0.25s ease',
                      overflow: 'hidden',
                      borderTop: expanded ? '1px solid var(--border)' : 'none',
                    }}
                  >
                    <div style={{ padding: '1.15rem 1.25rem 1.35rem', background: '#fafbfc' }}>
                      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 800 }}>Itinerary details</h3>
                      <div style={{ display: 'grid', gap: '0.45rem', marginBottom: '1rem', fontSize: '0.88rem', color: 'var(--text-main)' }}>
                        <div>
                          <span style={{ color: 'var(--text-muted)' }}>Route · </span>
                          {flight.departure} → {flight.arrival}
                        </div>
                        <div>
                          <span style={{ color: 'var(--text-muted)' }}>Fare · </span>${flight.price}
                        </div>
                        {flight.rawExtensions && flight.rawExtensions.length > 0 && (
                          <div>
                            <span style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-muted)' }}>Features</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.35rem' }}>
                              {flight.rawExtensions.slice(0, 8).map((x, idx) => (
                                <span
                                  key={`${flight.id}_ext_${idx}`}
                                  style={{
                                    background: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '999px',
                                    padding: '0.2rem 0.55rem',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {x}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {flight.segments && flight.segments.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', fontWeight: 800 }}>Segments</h4>
                          <div style={{ display: 'grid', gap: '0.5rem' }}>
                            {flight.segments.map((segment, idx) => (
                              <div
                                key={`${segment.flightNumber}-${idx}`}
                                style={{ background: 'white', borderRadius: '0.65rem', padding: '0.65rem 0.75rem', border: '1px solid var(--border)', fontSize: '0.84rem' }}
                              >
                                <strong>
                                  {segment.airline} {segment.flightNumber}
                                </strong>{' '}
                                · {segment.departure} ({segment.departureTime}) → {segment.arrival} ({segment.arrivalTime})
                                {typeof segment.duration === 'number' ? (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.25rem' }}>{segment.duration} min</div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {flight.layovers && flight.layovers.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', fontWeight: 800 }}>Layovers</h4>
                          <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                            {flight.layovers.map((layover) => (
                              <li key={`${layover.id}-${layover.duration}`}>
                                {layover.name} ({layover.id}) — {layover.duration} min{layover.overnight ? ' · overnight' : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.65rem' }}>
                          <button type="button" onClick={() => void addToInventory(flight)} disabled={savingFlight} style={{ ...btnSuccess, opacity: savingFlight ? 0.75 : 1 }}>
                            <Package size={16} aria-hidden />
                            {savingFlight ? 'Saving…' : 'Add to inventory'}
                          </button>
                          <button type="button" onClick={() => openQuote(flight)} style={{ ...btnPrimary }}>
                            <Mail size={16} aria-hidden />
                            New client quote
                          </button>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'stretch',
                            gap: '0.65rem',
                            padding: '0.85rem',
                            background: 'white',
                            borderRadius: '0.75rem',
                            border: '1px solid var(--border)',
                          }}
                        >
                          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                            <span style={{ ...fieldLabel, marginBottom: '0.35rem' }}>Existing client</span>
                            <select value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)} style={{ width: '100%' }}>
                              <option value="">Select client…</option>
                              {clients.map((client) => (
                                <option key={client.id} value={client.id}>
                                  {client.name}
                                  {client.email ? ` (${client.email})` : ' (no email)'}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <button
                              type="button"
                              disabled={!selectedClientId}
                              onClick={() => openQuote(flight, selectedClientId)}
                              style={{
                                ...btnDark,
                                opacity: selectedClientId ? 1 : 0.45,
                                cursor: selectedClientId ? 'pointer' : 'not-allowed',
                                height: '100%',
                                minHeight: '46px',
                              }}
                            >
                              Quote this client
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <FlightQuoteModal
        open={quoteOpen}
        onClose={() => {
          setQuoteOpen(false);
          setQuoteSearchContext(null);
          setQuoteFlight(null);
        }}
        agent={agent}
        client={selectedClient}
        onUpsertClient={upsertClient}
        preselectFlight={quoteFlight}
        initialSearchFromResults={quoteSearchContext}
      />
    </div>
  );
};

export default AdminFlightSearch;
